import { useState } from 'react';
import { FileText, CheckCircle, Building2, Calendar, DollarSign, MapPin } from 'lucide-react';

function RequestQuote() {
  const [formData, setFormData] = useState({
    // Contact Information
    name: '',
    email: '',
    phone: '',
    company: '',
    // Project Details
    projectType: '',
    industry: '',
    location: '',
    timeline: '',
    budget: '',
    projectDescription: '',
    // Additional Info
    preferredContact: 'email',
    howHeard: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const projectTypes = [
    'New Construction',
    'Renovation/Remodel',
    'Tenant Buildout',
    'Facility Maintenance',
    'Emergency Response',
    'National Rollout',
    'Site Development',
    'Other'
  ];

  const industries = [
    'Government & Defense',
    'Healthcare',
    'Commercial Real Estate',
    'Retail',
    'Restaurant & Hospitality',
    'Infrastructure',
    'Industrial & Manufacturing',
    'Education',
    'Energy',
    'Financial Institutions',
    'Convenience Stores',
    'Multi-Family Residential',
    'Other'
  ];

  const timelines = [
    'Immediate (Within 30 days)',
    '1-3 Months',
    '3-6 Months',
    '6-12 Months',
    'More than 12 Months',
    'Not Sure'
  ];

  const budgets = [
    'Under $50,000',
    '$50,000 - $100,000',
    '$100,000 - $250,000',
    '$250,000 - $500,000',
    '$500,000 - $1,000,000',
    '$1,000,000 - $5,000,000',
    'Over $5,000,000',
    'Not Sure'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          industry: '',
          location: '',
          timeline: '',
          budget: '',
          projectDescription: '',
          preferredContact: 'email',
          howHeard: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <FileText className="w-16 h-16 text-gold mx-auto mb-6" />
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Get Started</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Request a <span className="text-gold">Quote</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto">
            Tell us about your project and receive a customized quote from our experienced team. We'll respond within 24 business hours.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          {submitStatus === 'success' ? (
            <div className="text-center py-16">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                Quote Request <span className="text-gold">Submitted!</span>
              </h2>
              <p className="text-gray-300 text-lg mb-8">
                Thank you for your interest in Williams Diversified. Our team will review your project details and contact you within 24 business hours with a customized quote.
              </p>
              <button
                onClick={() => setSubmitStatus(null)}
                className="bg-gold text-black px-8 py-4 rounded-lg font-semibold hover:bg-gold/90 transition-colors"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-10">
              
              {/* Contact Information */}
              <div className="bg-black/50 border border-gold/20 rounded-lg p-8">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  <Building2 className="w-6 h-6 text-gold mr-3" />
                  Contact Information
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                      placeholder="John Smith"
                      data-testid="quote-name"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Company Name</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                      placeholder="ABC Corporation"
                      data-testid="quote-company"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                      placeholder="john@company.com"
                      data-testid="quote-email"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                      placeholder="(555) 123-4567"
                      data-testid="quote-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div className="bg-black/50 border border-gold/20 rounded-lg p-8">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  <FileText className="w-6 h-6 text-gold mr-3" />
                  Project Details
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Project Type *</label>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                      data-testid="quote-project-type"
                    >
                      <option value="">Select Project Type</option>
                      {projectTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Industry *</label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      required
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                      data-testid="quote-industry"
                    >
                      <option value="">Select Industry</option>
                      {industries.map(ind => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-gold" />
                      Project Location *
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                      placeholder="City, State or Multiple Locations"
                      data-testid="quote-location"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-gold" />
                      Expected Timeline
                    </label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                      data-testid="quote-timeline"
                    >
                      <option value="">Select Timeline</option>
                      {timelines.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-gray-300 mb-2 font-medium flex items-center">
                    <DollarSign className="w-4 h-4 mr-2 text-gold" />
                    Estimated Budget
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full bg-black border border-gold/30 rounded-lg px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                    data-testid="quote-budget"
                  >
                    <option value="">Select Budget Range</option>
                    {budgets.map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div className="mt-6">
                  <label className="block text-gray-300 mb-2 font-medium">Project Description *</label>
                  <textarea
                    name="projectDescription"
                    value={formData.projectDescription}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-black border border-gold/30 rounded-lg px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors resize-none"
                    placeholder="Please describe your project, including scope of work, special requirements, and any other relevant details..."
                    data-testid="quote-description"
                  />
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-black/50 border border-gold/20 rounded-lg p-8">
                <h2 className="text-2xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Additional Information
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Preferred Contact Method</label>
                    <div className="flex space-x-6">
                      <label className="flex items-center text-gray-300 cursor-pointer">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="email"
                          checked={formData.preferredContact === 'email'}
                          onChange={handleChange}
                          className="mr-2 accent-gold"
                        />
                        Email
                      </label>
                      <label className="flex items-center text-gray-300 cursor-pointer">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="phone"
                          checked={formData.preferredContact === 'phone'}
                          onChange={handleChange}
                          className="mr-2 accent-gold"
                        />
                        Phone
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">How did you hear about us?</label>
                    <input
                      type="text"
                      name="howHeard"
                      value={formData.howHeard}
                      onChange={handleChange}
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                      placeholder="Referral, Google, etc."
                      data-testid="quote-how-heard"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              {submitStatus === 'error' && (
                <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 text-center">
                  <p className="text-red-300">There was an error submitting your request. Please try again or contact us directly.</p>
                </div>
              )}

              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gold text-black px-12 py-4 rounded-lg font-semibold text-lg hover:bg-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="quote-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                </button>
                <p className="text-gray-500 text-sm mt-4">
                  By submitting this form, you agree to be contacted by Williams Diversified regarding your project.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-3xl font-bold text-white mb-12 text-center font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Why Request a Quote from <span className="text-gold">Williams Diversified</span>?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Free, No-Obligation Quote</h3>
              <p className="text-gray-400">Get a detailed estimate tailored to your specific project requirements at no cost.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Fast Response Time</h3>
              <p className="text-gray-400">Our team reviews every request and responds within 24 business hours.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Expert Consultation</h3>
              <p className="text-gray-400">Speak directly with experienced project managers who understand your industry.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default RequestQuote;
